import { Form, Row, Col, InputNumber, Button, TimePicker, Input, Select } from "antd";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { FormGroupIdentifier } from "components/FormGroupIdentifier/FormGroupIdentifier";
import { useEffect } from "react";
import moment from "moment";

interface StoreConfigFormProps {
  formInstance: any;
  onFinish: (values: Record<string, any>) => void;
  onCancel: () => void;
  onSubmit: () => void;
  values?: any;
  loading?: boolean;
  showStoreSelection?: boolean;
  cities?: any[];
  darkStores?: any[];
  selectedCityId?: string;
  selectedStoreId?: string;
  onCityChange?: (cityId: string) => void;
  onStoreChange?: (storeId: string) => void;
}

export const StoreConfigForm = (props: StoreConfigFormProps) => {
  useEffect(() => {
    if (props.values) {
      const formattedValues = {
        ...props.values,
        pickPackStartTime: props.values.pickPackStartTime
          ? moment(props.values.pickPackStartTime, "HH:mm:ss")
          : undefined,
        dispatchStartTime: props.values.dispatchStartTime
          ? moment(props.values.dispatchStartTime, "HH:mm:ss")
          : undefined,
      };
      props.formInstance.setFieldsValue(formattedValues);
    }
  }, [props.formInstance, props.values]);

  return (
    <Form
      form={props.formInstance}
      name="store-config-form"
      layout="vertical"
      style={{ width: "100%" }}
      onFinish={props.onFinish}
    >
      <Row>
        {props.showStoreSelection && (
          <>
            <Col xs={24}>
              <FormGroupIdentifier>Select Store</FormGroupIdentifier>
            </Col>

            <Col xs={24}>
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="cityId"
                    label="City"
                    rules={[
                      {
                        required: true,
                        message: "Please select a city!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select city"
                      style={{ width: "100%" }}
                      onChange={props.onCityChange}
                      filterOption={(input, option) => {
                        return option?.children?.toString()
                          .toLowerCase()
                          .includes(input?.toLowerCase() ?? "") ?? false;
                      }}
                    >
                      {(props.cities ?? []).map((city) => (
                        <Select.Option value={city.id} key={city.id}>
                          {city.name} || {city.id}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {props.selectedCityId && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="storeId"
                      label="Store ID"
                      rules={[
                        {
                          required: true,
                          message: "Please select a store!",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Select store"
                        style={{ width: "100%" }}
                        value={props.selectedStoreId}
                        onChange={props.onStoreChange}
                        filterOption={(input, option) => {
                          return option?.children?.toString()
                            .toLowerCase()
                            .includes(input?.toLowerCase() ?? "") ?? false;
                        }}
                      >
                        {(props.darkStores ?? [])
                          .filter(store => store?.cityId?.toString() == props.selectedCityId)
                          .map((storeInfo) => (
                            <Select.Option value={storeInfo.id} key={storeInfo.id}>
                              {storeInfo.name} || {storeInfo.id}
                            </Select.Option>
                          ))
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Col>
          </>
        )}

        <Col xs={24}>
          <FormGroupIdentifier>Store Configuration</FormGroupIdentifier>
        </Col>

        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item
                name="storeId"
                label="Store ID"
              >
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <FormGroupIdentifier>Workforce</FormGroupIdentifier>
        </Col>

        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item
                name="pickers"
                label="Pickers"
                rules={[
                  {
                    required: true,
                    message: "Please input number of pickers!",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} precision={0} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="packers"
                label="Packers"
                rules={[
                  {
                    required: true,
                    message: "Please input number of packers!",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} precision={0} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="dispatchers"
                label="Dispatchers"
                rules={[
                  {
                    required: true,
                    message: "Please input number of dispatchers!",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} precision={0} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="helperCount"
                label="Helper Count"
                rules={[
                  {
                    required: true,
                    message: "Please input helper count!",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} precision={0} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <FormGroupIdentifier>Vehicles</FormGroupIdentifier>
        </Col>

        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item
                name="evCount"
                label="EV Count"
                rules={[
                  {
                    required: true,
                    message: "Please input EV count!",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} precision={0} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="autoCount"
                label="Auto Count"
                rules={[
                  {
                    required: true,
                    message: "Please input auto count!",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} precision={0} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="bikeCount"
                label="Bike Count"
                rules={[
                  {
                    required: true,
                    message: "Please input bike count!",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} precision={0} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="evL3Count"
                label="EV L3 Count"
                rules={[
                  {
                    required: true,
                    message: "Please input EV L3 count!",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} precision={0} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="dockCount"
                label="Dock Count"
                rules={[
                  {
                    required: true,
                    message: "Please input dock count!",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} precision={0} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <FormGroupIdentifier>Average Times & Distances</FormGroupIdentifier>
        </Col>

        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="interSocietyAvgDistanceKm"
                label="Inter Society Avg Distance (km)"
                rules={[
                  {
                    required: true,
                    message: "Please input inter society avg distance!",
                  },
                ]}
              >
                <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="interSocietyAvgTravelTimeMins"
                label="Inter Society Avg Travel Time (mins)"
                rules={[
                  {
                    required: true,
                    message: "Please input inter society avg travel time!",
                  },
                ]}
              >
                <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="avgDeliveryTimePerOrderMins"
                label="Avg Delivery Time Per Order (mins)"
                rules={[
                  {
                    required: true,
                    message: "Please input avg delivery time per order!",
                  },
                ]}
              >
                <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="avgEntryTimePerSocietyMins"
                label="Avg Entry Time Per Society (mins)"
                rules={[
                  {
                    required: true,
                    message: "Please input avg entry time per society!",
                  },
                ]}
              >
                <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="avgPickTimePerLineItemMins"
                label="Avg Pick Time Per Line Item (mins)"
                rules={[
                  {
                    required: true,
                    message: "Please input avg pick time per line item!",
                  },
                ]}
              >
                <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="avgPackTimePerOrderMins"
                label="Avg Pack Time Per Order (mins)"
                rules={[
                  {
                    required: true,
                    message: "Please input avg pack time per order!",
                  },
                ]}
              >
                <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="avgDispatchTimePerOrderMins"
                label="Avg Dispatch Time Per Order (mins)"
                rules={[
                  {
                    required: true,
                    message: "Please input avg dispatch time per order!",
                  },
                ]}
              >
                <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <FormGroupIdentifier>Timing Configuration</FormGroupIdentifier>
        </Col>

        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="pickPackStartTime"
                label="Pick Pack Start Time"
                rules={[
                  {
                    required: true,
                    message: "Please select pick pack start time!",
                  },
                ]}
              >
                <TimePicker
                  format="HH:mm"
                  style={{ width: "100%" }}
                  showSecond={false}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="dispatchStartTime"
                label="Dispatch Start Time"
                rules={[
                  {
                    required: true,
                    message: "Please select dispatch start time!",
                  },
                ]}
              >
                <TimePicker
                  format="HH:mm"
                  style={{ width: "100%" }}
                  showSecond={false}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <AppActionStrip>
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button
          type="primary"
          onClick={props.onSubmit}
          loading={props.loading}
        >
          Save
        </Button>
      </AppActionStrip>
    </Form>
  );
};
